/**
 * THEO Workflow System - Firestore Document Types
 *
 * Firestore Collections:
 * - customers/{customerId}
 * - deals/{dealId}
 * - provisioning_items/{itemId}
 * - tech_requests/{requestId}
 * - parts_catalog/{partId}
 * - presite_parts/{selectionId}
 * - quote_templates/{templateId}
 * - task_queue/{taskId}
 */

import { Timestamp } from 'firebase/firestore';
import { DEAL_CATEGORY_LABELS as SHARED_DEAL_CATEGORY_LABELS } from '@shared/domain/deal-category-labels';
import type { StarlinkSpeedTier } from '@shared/domain/starlink-kit';
import type { PlaygroundQuoteItem, QuoteLineItem, QuoteScope } from './schema';
// Proposal Builder V2 (S1) — content type shared with functions/. Type-only.
import type { ProposalV2Content } from '@shared/proposals/types';
import type { Attachment } from './attachment.types';

// ============================================================================
// ENUMS
// ============================================================================

export enum DealCategory {
  SMART_HOME = 'SMART_HOME',
  NBN = 'NBN',
  STARLINK = 'STARLINK',
  WIFI_EXTENSION = 'WIFI_EXTENSION',
  // NOTE: the SECURITY key/value is retained for backward-compat with every
  // existing deal in Firestore + the keyword-detection rules. Its display
  // label is "Surveillance" (see DEAL_CATEGORY_LABELS) — do NOT rename the
  // enum value to SURVEILLANCE without a data migration.
  SECURITY = 'SECURITY',
  ACCESS_CONTROL = 'ACCESS_CONTROL',
  AV = 'AV',
  IT = 'IT',
  VOIP = 'VOIP',
  FEE_FOR_SERVICE = 'FEE_FOR_SERVICE',
}

/**
 * Phase 0 (trade-pack architecture): widened category type that accommodates
 * pack-introduced categories (e.g. an electrician pack adding
 * 'switchboard-upgrade'). Legacy enum values continue to satisfy the type
 * unchanged because TS string enums ARE strings at runtime
 * (DealCategory.NBN === 'NBN').
 *
 * Use `DealCategoryKey` at pack-aware boundaries. Existing
 * `DealCategory`-typed code continues to work for telco categories — pack
 * categories simply don't match those enum values, which is the correct
 * behaviour (pack categories shouldn't render NBN-specific UI).
 */
export type DealCategoryKey = DealCategory | string;

/**
 * Human-readable labels for DealCategory enum values.
 *
 * Wave 1: the VALUES live in shared/src/domain/deal-category-labels.ts —
 * one source for client + server email labels. This is the client's typed
 * view over the shared map (every DealCategory key is present there; the
 * shared module's doc carries the SECURITY→"Surveillance" rename warning).
 */
export const DEAL_CATEGORY_LABELS: Record<DealCategory, string> =
  SHARED_DEAL_CATEGORY_LABELS;

/**
 * PandaDoc Scoped Payload (moved from quote-builder.service in Wave 2 —
 * types must not import from services)
 * Structured format for sending scoped quotes to PandaDoc via Zapier
 * COMPLETE - Ready for PandaDoc without manual editing
 */
export interface PandaDocScopedPayload {
  // Quote metadata
  scopeSummary: string;
  quoteDate: string; // ISO date string

  // Customer info (if available from job)
  customer?: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  };

  // Quote sections with items + labour
  sections: {
    sectionName: string;
    sectionDescription?: string;
    sectionNotes?: string; // Per-section notes from tech
    lineItems: QuoteLineItem[];
    // Labour for this section
    labour?: {
      type: '1-tech' | '2-techs' | '2-senior-techs';
      hours: number;
      rate: number;
      total: number;
    };
    sectionSubtotal: number;
  }[];

  // Totals
  subtotal: number;
  tax: number;
  grandTotal: number;
}

export enum StarlinkKitOption {
  TECHERY = 'TECHERY',   // Techery supplies the Starlink kit
  BYO = 'BYO',           // Customer brings their own Starlink kit
}

export enum DealComplexity {
  STANDARD = 'STANDARD',
  COMPLEX = 'COMPLEX',
}

/**
 * Deal Workflow Stages
 * Replaces the old linear pipeline with a comprehensive workflow
 */
export enum DealStage {
  INTAKE = 'INTAKE',
  PRESITE = 'PRESITE',
  QUOTING = 'QUOTING',
  PROPOSAL_SENT = 'PROPOSAL_SENT',
  WON = 'WON',
  PROVISIONING = 'PROVISIONING',
  SCHEDULING = 'SCHEDULING',
  REVIEW = 'REVIEW',
  BILLING = 'BILLING',
  COMPLETED = 'COMPLETED',
  LOST = 'LOST',
}

/** Human-readable labels for DealStage */
export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  [DealStage.INTAKE]: 'Intake',
  [DealStage.PRESITE]: 'Pre-Site',
  [DealStage.QUOTING]: 'Quoting',
  [DealStage.PROPOSAL_SENT]: 'Proposal Sent',
  [DealStage.WON]: 'Won',
  [DealStage.PROVISIONING]: 'Provisioning',
  [DealStage.SCHEDULING]: 'Scheduling',
  [DealStage.REVIEW]: 'Review',
  [DealStage.BILLING]: 'Billing',
  [DealStage.COMPLETED]: 'Completed',
  [DealStage.LOST]: 'Lost',
};

/** Ordered list of deal stages (excluding LOST) for progress rail */
export const DEAL_STAGE_ORDER: DealStage[] = [
  DealStage.INTAKE,
  DealStage.PRESITE,
  DealStage.QUOTING,
  DealStage.PROPOSAL_SENT,
  DealStage.WON,
  DealStage.PROVISIONING,
  DealStage.SCHEDULING,
  DealStage.REVIEW,
  DealStage.BILLING,
  DealStage.COMPLETED,
];

export enum JobType {
  PRESITE = 'PRESITE', // Presite/scoping job
  REGULAR = 'REGULAR', // Standard installation job
  SERVICE_CALL = 'SERVICE_CALL', // Service/repair job
}

export enum TechRequestPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum TechRequestStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum ProvisioningItemStatus {
  // === New statuses (V107.52.18+) ===
  NOT_ORDERED = 'NOT_ORDERED',
  QUOTE_REQUESTED = 'QUOTE_REQUESTED', // Price requested from the supplier, not yet ordered
  HELD_AWAITING_DEPOSIT = 'HELD_AWAITING_DEPOSIT', // Order on hold pending customer deposit
  AWAITING_FUNDS = 'AWAITING_FUNDS',
  AWAITING_TECH_REQUEST = 'AWAITING_TECH_REQUEST',
  ORDERED = 'ORDERED',
  SHIPPED = 'SHIPPED',
  AT_OFFICE = 'AT_OFFICE',
  DELIVERED = 'DELIVERED',

  // === Legacy values (backward compat — mapped on read) ===
  PENDING = 'PENDING',
  IN_STOCK = 'IN_STOCK',
  ASSIGNED = 'ASSIGNED',
}

/** Migrate legacy provisioning status values to new enum on read */
export function migrateProvisioningStatus(status: string): ProvisioningItemStatus {
  switch (status) {
    case 'PENDING': return ProvisioningItemStatus.NOT_ORDERED;
    case 'IN_STOCK': return ProvisioningItemStatus.AT_OFFICE;
    case 'ASSIGNED': return ProvisioningItemStatus.AT_OFFICE;
    default: return status as ProvisioningItemStatus;
  }
}

/**
 * True when a quote / review / proposal line represents LABOUR rather than a
 * provisionable good or service. Labour must NEVER be written as a
 * `provisioning_items` doc — it carries no gear to order/pick, so it only
 * clutters the provisioning board (and would otherwise hold up the
 * scheduling-readiness gate while sitting at NOT_ORDERED).
 *
 * Labour is billed entirely off the DEAL — scope-level `laborAmount` /
 * `laborType` × `laborHours`, and the materialized "Labour — {scope}" reviewed
 * items — never off `provisioning_items`. So filtering it here has zero effect
 * on billing, invoices, deal value, or MRR. Same suppression seam as
 * `hideFromProvisioning` (items stay in deal value + billing, dropped from
 * provisioning only).
 *
 * Matches every way the codebase materializes labour into a named line:
 *  - `category` of 'Labour' / 'Labor' — stamped by
 *    `quote-review.startQuoteReview`, `onProposalAccepted.buildReviewedItems`,
 *    and `push-invoice.scopeLabourToCanonicalItem`.
 *  - `name` beginning with a 'labour'/'labor' token then end-of-string or a
 *    separator: "Labour — {scope}", bare "Labour", and the field-scope
 *    `makeExtraLabourLineItem` "Labour — 2-techs × 4hr" form (which carries NO
 *    category, so the name check is the only thing that catches it).
 *
 * The leading-token + separator anchor keeps genuine hardware that merely
 * contains the substring (e.g. a "Labour-saving relay") from false-matching on
 * name alone — such an item would be disambiguated by its own category.
 *
 * Structural param (not a concrete item type) so `PlaygroundQuoteItem` /
 * `ReviewedQuoteItem` (which use `name`) AND `ProvisioningItem` (which uses
 * `itemName` and has no top-level `category`) all pass without an adapter.
 */
export function isLabourLineItem(item: {
  name?: string | null;
  itemName?: string | null;
  category?: string | null;
}): boolean {
  const category = (item.category || '').toString().toLowerCase().trim();
  if (category === 'labour' || category === 'labor') return true;
  const name = (item.name || item.itemName || '').toString().toLowerCase().trim();
  // 'labour'/'labor' at the start, then end-of-string OR a separator
  // (em-dash, en-dash, hyphen, colon — every form the materializers emit).
  return /^lab(?:our|or)\b\s*(?:$|[—–\-:])/.test(name);
}

/**
 * Classify a provisioning item as hardware or service.
 *
 * Uses the explicit `provisioningCategory` field if set, otherwise
 * falls back to heuristic classification based on specialWorkflow,
 * billingType, and item name patterns.
 *
 * Service items: activations, porting, recurring billing, ISP plans,
 *   software licences, anything with "activate", "plan", "service" etc.
 * Hardware items: everything else (APs, switches, cameras, cables, etc.)
 */
export function classifyProvisioningItem(item: ProvisioningItem): 'hardware' | 'service' {
  // Explicit category takes precedence
  if (item.provisioningCategory) return item.provisioningCategory;

  // Special workflows
  if (item.specialWorkflow === 'activation') return 'service';
  if (item.specialWorkflow === 'porting') return 'service';

  // Recurring billing items are services
  if (item.billingType === 'recurring') return 'service';

  // Name-based heuristic for common service patterns
  const name = (item.itemName || '').toLowerCase();

  // Physical-hardware nouns win BEFORE the service-keyword scan below. Those
  // keywords are substring matches on generic words ('licence', 'plan',
  // 'service'), so a name that unambiguously describes a piece of gear would
  // otherwise be mis-filed as a service and disappear from the Hardware panel.
  // The original offender:
  //   "Ubiquiti UniFi Protect AI Licence Plate Recognition Camera"
  // — "licence" (there to catch software licences) matched and pushed an LPR
  // camera into the Services & ISP tab. "camera" winning first keeps it as
  // hardware. Keep this list tight + unambiguous so it can't create the
  // inverse mis-classification (a real service read as hardware).
  const hardwareNouns = [
    'camera', 'intercom', 'switch', 'pole', 'cable', 'antenna', 'dish',
    'bracket', 'nvr', 'recorder', 'keypad', 'siren', 'doorbell', 'injector',
    'enclosure',
  ];
  if (hardwareNouns.some((kw) => name.includes(kw))) return 'hardware';

  const serviceKeywords = [
    'activate', 'activation', 'plan', 'service', 'subscription',
    'licence', 'license', 'porting', 'port number',
    'nbn order', 'starlink order', 'voip order', 'isp',
    'monthly', 'recurring',
    // Service-side one-off lines (connection / setup fees) that genuinely bill
    // one-time, so the billingType escape above doesn't fire. Without these a
    // "NBN Connection Fee" defaulted to hardware and blocked scheduling
    // readiness (it never ships). Kept as specific fee PHRASES — not bare
    // 'connection'/'setup' — so a "Connection Block" patch panel or a
    // "Router configuration service" isn't mis-filed. Gear like "Starlink Kit"
    // matches no keyword and falls through to the hardware default below.
    'connection fee', 'new connection', 'setup fee', 'service fee',
  ];
  if (serviceKeywords.some((kw) => name.includes(kw))) return 'service';

  // Default: hardware
  return 'hardware';
}

// ============================================================================
// SERVICE PROVISIONING WORKFLOWS (Services & ISP panel)
// ----------------------------------------------------------------------------
// Per-service-type stage flows that REPLACE the generic order/ship/receive
// status flow for ISP / recurring service items. Each service type (NBN,
// VoIP, Starlink, …) has its own ordered list of stages shown as a stepper
// on the Services & ISP tab.
//
// The deal's scheduling-readiness gate keys off the underlying
// `ProvisioningItemStatus`, which is DERIVED from the active stage's `kind`
// (see `serviceStageKindToStatus`): a `complete` stage marks the item
// DELIVERED (counts as "done / ready" → lets the deal auto-advance to
// SCHEDULING), every other kind maps to NOT_ORDERED (blocks). This is how
// "NBN order ready" / "VoIP port confirmed" send a job out of provisioning.
// ============================================================================

export type ServiceProvisioningStageKind = 'initial' | 'active' | 'complete' | 'held';

export interface ServiceProvisioningStageDef {
  /** Stable id stored on `ProvisioningItem.serviceStage`. */
  id: string;
  /** Short label shown in the stepper. */
  label: string;
  /** Drives the scheduling-gate status + the UI affordance. */
  kind: ServiceProvisioningStageKind;
  /** Optional helper text shown under the stepper. */
  description?: string;
  /** Capture a date/time (`serviceScheduledAt`) when entering this stage —
   *  e.g. the VoIP port cutover date. */
  requiresDateTime?: boolean;
  /** Set programmatically only (not user-pickable) — e.g. the Starlink
   *  "provisioned with kit" auto-clear. */
  autoOnly?: boolean;
}

export interface ServiceProvisioningWorkflowDef {
  /** Canonical workflow key stored on `ProvisioningItem.serviceWorkflowType`. */
  key: string;
  /** Display label for the workflow. */
  label: string;
  stages: ServiceProvisioningStageDef[];
}

/** Built-in service-provisioning workflows, keyed by canonical workflow key.
 *  OptiComm reuses the NBN workflow (resolved in `resolveServiceWorkflowKey`). */
export const SERVICE_PROVISIONING_WORKFLOWS: Record<string, ServiceProvisioningWorkflowDef> = {
  NBN: {
    key: 'NBN',
    label: 'NBN / fixed-line connection',
    stages: [
      { id: 'nbn_availability', label: 'Checked availability', kind: 'initial', description: 'Availability confirmed for the address.' },
      { id: 'nbn_order_ready', label: 'Order ready for checkout', kind: 'complete', description: 'Order placed / ready — service provisioning done.' },
      { id: 'nbn_awaiting', label: 'Awaiting customer / NBN', kind: 'held', description: 'Parked pending the customer or NBN Co.' },
    ],
  },
  VoIP: {
    key: 'VoIP',
    label: 'VoIP number porting',
    stages: [
      { id: 'voip_awaiting_docs', label: 'Awaiting porting docs', kind: 'initial', description: 'Waiting on porting paperwork from the customer.' },
      { id: 'voip_port_submitted', label: 'Port request submitted', kind: 'active', description: 'Port request lodged with the carrier.' },
      { id: 'voip_port_confirmed', label: 'Port confirmed', kind: 'complete', requiresDateTime: true, description: 'Port confirmed — set the cutover date/time. Sends the job to scheduling.' },
      { id: 'voip_awaiting', label: 'Awaiting parts / customer', kind: 'held', description: 'Parked pending parts or the customer.' },
    ],
  },
  Starlink: {
    key: 'Starlink',
    label: 'Starlink service',
    stages: [
      { id: 'starlink_pending', label: 'Ordered with kit', kind: 'initial', description: 'Auto-ordered when the Starlink kit is provisioned in Hardware.' },
      { id: 'starlink_cleared', label: 'Provisioned with kit', kind: 'complete', autoOnly: true, description: 'Cleared automatically once the Starlink kit is ordered.' },
    ],
  },
  DEFAULT: {
    key: 'DEFAULT',
    label: 'Service',
    stages: [
      { id: 'service_in_progress', label: 'In progress', kind: 'initial' },
      { id: 'service_ready', label: 'Ready', kind: 'complete', description: 'Service provisioning done.' },
      { id: 'service_awaiting', label: 'Awaiting customer', kind: 'held' },
    ],
  },
};

/** Map a free-text serviceType / dealCategory / module string to a workflow key. */
function normaliseServiceWorkflowKey(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const v = raw.toString().trim().toLowerCase();
  if (!v) return undefined;
  if (v.includes('starlink')) return 'Starlink';
  if (v.includes('voip') || v.includes('sip')) return 'VoIP';
  if (
    v.includes('nbn') || v.includes('opticomm') ||
    v.includes('broadband') || v.includes('fibre') || v.includes('fiber')
  ) return 'NBN';
  return undefined;
}

/** Resolve the service workflow key for a provisioning item. Falls back to
 *  'DEFAULT' so every service item gets a workable stepper. */
export function resolveServiceWorkflowKey(
  item: Pick<ProvisioningItem, 'serviceWorkflowType' | 'metadata' | 'itemName' | 'specialWorkflow'>
): string {
  // 1. Explicit field wins.
  if (item.serviceWorkflowType && SERVICE_PROVISIONING_WORKFLOWS[item.serviceWorkflowType]) {
    return item.serviceWorkflowType;
  }
  const meta = (item.metadata || {}) as Record<string, unknown>;
  const sd = meta.serviceDetails as Record<string, unknown> | undefined;
  // 2. serviceDetails.dealCategory / serviceType (Quote Review "Add Service").
  const fromDetails =
    normaliseServiceWorkflowKey(sd?.dealCategory as string) ||
    normaliseServiceWorkflowKey(sd?.serviceType as string);
  if (fromDetails) return fromDetails;
  // 3. metadata.serviceModule (DealCategory).
  const fromModule = normaliseServiceWorkflowKey(meta.serviceModule as string);
  if (fromModule) return fromModule;
  // 4. specialWorkflow hint.
  if (item.specialWorkflow === 'porting') return 'VoIP';
  // 5. name heuristic.
  const fromName = normaliseServiceWorkflowKey(item.itemName);
  if (fromName) return fromName;
  return 'DEFAULT';
}

/** Workflow definition for a key (always returns one — DEFAULT fallback). */
export function getServiceWorkflowDef(key: string): ServiceProvisioningWorkflowDef {
  return SERVICE_PROVISIONING_WORKFLOWS[key] || SERVICE_PROVISIONING_WORKFLOWS.DEFAULT;
}

/** True when an item should render the per-service stage stepper (Services
 *  & ISP tab). Mirrors the panel classifier. */
export function isServiceWorkflowItem(item: ProvisioningItem): boolean {
  return classifyProvisioningItem(item) === 'service';
}

/** The stage currently active on a service item. Back-compat: when
 *  `serviceStage` is unset (legacy items predating this rework) a sensible
 *  stage is inferred from the underlying status so old rows still render a
 *  coherent stepper. */
export function getServiceStageDef(item: ProvisioningItem): ServiceProvisioningStageDef {
  const wf = getServiceWorkflowDef(resolveServiceWorkflowKey(item));
  if (item.serviceStage) {
    const found = wf.stages.find((s) => s.id === item.serviceStage);
    if (found) return found;
  }
  // Legacy: derive from status.
  const migrated = migrateProvisioningStatus(item.status);
  if (PROVISIONING_READY_STATUSES.includes(migrated)) {
    return wf.stages.find((s) => s.kind === 'complete') || wf.stages[0];
  }
  if (
    migrated === ProvisioningItemStatus.AWAITING_FUNDS ||
    migrated === ProvisioningItemStatus.HELD_AWAITING_DEPOSIT ||
    migrated === ProvisioningItemStatus.AWAITING_TECH_REQUEST ||
    migrated === ProvisioningItemStatus.QUOTE_REQUESTED
  ) {
    return wf.stages.find((s) => s.kind === 'held') || wf.stages[0];
  }
  return wf.stages[0];
}

/** Underlying provisioning status that drives the deal's scheduling gate for
 *  a given stage kind. `complete` → DELIVERED (ready), everything else →
 *  NOT_ORDERED (blocks). */
export function serviceStageKindToStatus(kind: ServiceProvisioningStageKind): ProvisioningItemStatus {
  return kind === 'complete' ? ProvisioningItemStatus.DELIVERED : ProvisioningItemStatus.NOT_ORDERED;
}

/** The initial stage id for a workflow key (used at generation time). */
export function initialServiceStageId(workflowKey: string): string {
  const wf = getServiceWorkflowDef(workflowKey);
  const initial = wf.stages.find((s) => s.kind === 'initial') || wf.stages[0];
  return initial.id;
}

/** Display labels for provisioning statuses */
export const PROVISIONING_STATUS_LABELS: Record<string, string> = {
  [ProvisioningItemStatus.NOT_ORDERED]: 'Not Ordered',
  [ProvisioningItemStatus.QUOTE_REQUESTED]: 'Quote Requested',
  [ProvisioningItemStatus.HELD_AWAITING_DEPOSIT]: 'Held — Awaiting Deposit',
  [ProvisioningItemStatus.AWAITING_FUNDS]: 'Awaiting Funds',
  [ProvisioningItemStatus.AWAITING_TECH_REQUEST]: 'Awaiting Tech Request',
  [ProvisioningItemStatus.ORDERED]: 'Ordered',
  [ProvisioningItemStatus.SHIPPED]: 'Shipped',
  [ProvisioningItemStatus.AT_OFFICE]: 'At Office',
  [ProvisioningItemStatus.DELIVERED]: 'Delivered (no order needed)',
  // Legacy fallbacks
  [ProvisioningItemStatus.PENDING]: 'Not Ordered',
  [ProvisioningItemStatus.IN_STOCK]: 'At Office',
  [ProvisioningItemStatus.ASSIGNED]: 'At Office',
};

/** Provisioning status display config: color, icon hint, and sort order */
export const PROVISIONING_STATUS_CONFIG: Record<string, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
  sortOrder: number;
}> = {
  [ProvisioningItemStatus.NOT_ORDERED]: {
    label: 'Not Ordered',
    color: 'text-red-500 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-500/10',
    borderColor: 'border-red-200 dark:border-red-500/20',
    iconColor: 'text-red-500 dark:text-red-400',
    sortOrder: 0,
  },
  [ProvisioningItemStatus.QUOTE_REQUESTED]: {
    label: 'Quote Requested',
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-500/10',
    borderColor: 'border-pink-200 dark:border-pink-500/20',
    iconColor: 'text-pink-500 dark:text-pink-400',
    sortOrder: 1,
  },
  [ProvisioningItemStatus.HELD_AWAITING_DEPOSIT]: {
    label: 'Held — Awaiting Deposit',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-500/10',
    borderColor: 'border-orange-200 dark:border-orange-500/20',
    iconColor: 'text-orange-500 dark:text-orange-400',
    sortOrder: 2,
  },
  [ProvisioningItemStatus.AWAITING_FUNDS]: {
    label: 'Awaiting Funds',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-500/10',
    borderColor: 'border-amber-200 dark:border-amber-500/20',
    iconColor: 'text-amber-500 dark:text-amber-400',
    sortOrder: 3,
  },
  [ProvisioningItemStatus.AWAITING_TECH_REQUEST]: {
    label: 'Awaiting Tech',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-500/10',
    borderColor: 'border-purple-200 dark:border-purple-500/20',
    iconColor: 'text-purple-500 dark:text-purple-400',
    sortOrder: 4,
  },
  [ProvisioningItemStatus.ORDERED]: {
    label: 'Ordered',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-500/10',
    borderColor: 'border-indigo-200 dark:border-indigo-500/20',
    iconColor: 'text-indigo-500 dark:text-indigo-400',
    sortOrder: 5,
  },
  [ProvisioningItemStatus.SHIPPED]: {
    label: 'Shipped',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-500/10',
    borderColor: 'border-blue-200 dark:border-blue-500/20',
    iconColor: 'text-blue-500 dark:text-blue-400',
    sortOrder: 6,
  },
  [ProvisioningItemStatus.AT_OFFICE]: {
    label: 'At Office',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-500/10',
    borderColor: 'border-emerald-200 dark:border-emerald-500/20',
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    sortOrder: 7,
  },
  [ProvisioningItemStatus.DELIVERED]: {
    label: 'Delivered',
    color: 'text-slate-500 dark:text-slate-400',
    bgColor: 'bg-slate-50 dark:bg-slate-500/10',
    borderColor: 'border-slate-200 dark:border-slate-500/20',
    iconColor: 'text-slate-500 dark:text-slate-400',
    sortOrder: 8,
  },
};

/** Active provisioning statuses (i.e., items that count as "ready" for scheduling) */
export const PROVISIONING_READY_STATUSES: ProvisioningItemStatus[] = [
  ProvisioningItemStatus.AT_OFFICE,
  ProvisioningItemStatus.DELIVERED,
];

/** Provisioning status progression order for display */
export const PROVISIONING_STATUS_ORDER: ProvisioningItemStatus[] = [
  ProvisioningItemStatus.NOT_ORDERED,
  ProvisioningItemStatus.QUOTE_REQUESTED,
  ProvisioningItemStatus.HELD_AWAITING_DEPOSIT,
  ProvisioningItemStatus.AWAITING_FUNDS,
  ProvisioningItemStatus.AWAITING_TECH_REQUEST,
  ProvisioningItemStatus.ORDERED,
  ProvisioningItemStatus.SHIPPED,
  ProvisioningItemStatus.AT_OFFICE,
  ProvisioningItemStatus.DELIVERED,
];

/**
 * Tally items by (migrated) status, in pipeline order, statuses with no items
 * dropped — the data behind every provisioning progress bar.
 */
export function tallyProvisioningStatuses(
  items: Pick<ProvisioningItem, 'status'>[],
): { status: ProvisioningItemStatus; count: number }[] {
  const counts = new Map<ProvisioningItemStatus, number>();
  items.forEach((i) => {
    const s = migrateProvisioningStatus(i.status);
    counts.set(s, (counts.get(s) || 0) + 1);
  });
  return PROVISIONING_STATUS_ORDER
    .filter((s) => (counts.get(s) || 0) > 0)
    .map((s) => ({ status: s, count: counts.get(s)! }));
}

export enum JobStatus {
  UNSCHEDULED = 'UNSCHEDULED',
  SCHEDULED = 'SCHEDULED',
  TRAVELING = 'TRAVELING',
  IN_PROGRESS = 'IN_PROGRESS',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
}

// Legacy job type field (different from JobType enum)
export enum LegacyJobTypeField {
  JOB = 'job',
  TASK = 'task',
}

// Legacy job type categories
export enum LegacyJobCategory {
  INSTALL = 'Install',
  FAULT = 'Fault',
  PRE_SITE = 'Pre-Site',
  TASK = 'Task',
}

// Provisioning status for legacy jobs
export enum LegacyProvisioningStatus {
  NOT_ORDERED = 'Not Ordered',
  ORDERED = 'Ordered',
  READY = 'Ready',
}

// ============================================================================
// SERVICE MODULE TYPES
// ============================================================================

/** Data for a single service module attached to a deal */
export interface ServiceModuleData {
  presiteRequired: boolean;
  presiteCompleted: boolean;
  intakeFields: ServiceIntakeFields;
  presiteFields: Record<string, unknown>;
  postQuoteFields: Record<string, unknown>;
  provisioningFields: Record<string, unknown>;
  status: 'intake' | 'presite' | 'quoting' | 'provisioning' | 'complete';
}

/** Union of all service-specific intake fields */
export type ServiceIntakeFields =
  | NBNIntakeFields
  | StarlinkIntakeFields
  | VoIPIntakeFields
  | ITIntakeFields
  | SmartHomeIntakeFields
  | SecurityIntakeFields
  | WiFiExtensionIntakeFields;

export interface NBNIntakeFields {
  byoModem: boolean;
  servicePlan: string;
  notes?: string;
}

export interface StarlinkIntakeFields {
  starlinkKit: StarlinkKitOption;
  starlinkEmail: string;
  servicePlan: string;
  /** True when the customer runs the Starlink service on their OWN account
   *  ("S/M" kit) rather than Techery holding the account and rebilling.
   *  Independent of `starlinkKit` — a Techery-supplied kit can still land on
   *  a self-managed service. Drives the provisioning kit label. */
  starlinkSelfManaged?: boolean;
  starlinkModel?: 'Standard' | 'Mini';
  mountType?: 'Pole' | 'Wall' | 'Roof' | 'Ground' | 'Pipe Adaptor';
  poleRequired?: boolean;
  ethernetAdaptor?: boolean;
  notes?: string;
}

export interface VoIPIntakeFields {
  byoHandsets: boolean;
  portingRequired: boolean;
  numbersToPort: string[];
  complexity: 'Simple' | 'Complex';
  notes?: string;
}

export interface ITIntakeFields {
  description: string;
  notes?: string;
}

export interface SmartHomeIntakeFields {
  description: string;
  notes?: string;
}

export interface SecurityIntakeFields {
  estimatedCameraCount: string;
  cameraInfo: string;
  notes?: string;
}

export interface WiFiExtensionIntakeFields {
  wifiType: 'Deco' | 'UniFi AP' | 'NanoBeam' | string;
  notes?: string;
}

// ============================================================================
// WORKFLOW TRACKING TYPES
// ============================================================================

export interface StageHistoryEntry {
  stage: DealStage;
  enteredAt: string;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
}

export interface QuoteVersion {
  version: number;
  items: PlaygroundQuoteItem[];
  scopes?: QuoteScope[];            // Scoped items (V107.52.12+)
  scopeSummary?: string;
  subtotal: number;
  gst: number;
  total: number;
  monthlyTotal: number;
  status: 'draft' | 'sent' | 'signed' | 'archived';
  createdAt: string;
  sentAt?: string;
  signedAt?: string;

  // ── Proposal Builder V2 (additive) ──
  /** The deal's `proposalV2` narrative at pin time (V2 deals only). */
  v2Content?: ProposalV2Content;
  /** Office label for the pinned version ("Before the 4K swap"). */
  label?: string;
  /** `total` (inc-GST one-time) per option id at pin time — V2 deals with options. */
  totalByOption?: Record<string, number>;
  /** S9: the deal's `quoteDiscountPercent` at pin time (V2 pins). Restore writes it back; absent (V1 versions) → the discount is left as it is. */
  discountPercent?: number;
  /** S9: who pinned it (`getSession().userId`). */
  createdBy?: string;
  /** S9: their display name at pin time — the History panel shows it. */
  createdByName?: string;
}

/**
 * Reviewed Quote Item
 * Tracks approval/rejection of quote items after customer acceptance
 * Only approved items flow through to provisioning
 */
/**
 * ISP / recurring-service details captured against a quote-review line item.
 *
 * Set when the office adds a service via the "Add Service" flow in the Quote
 * Review modal (NBN plan + speeds, Starlink plan, VoIP, 4G, etc.). Carries
 * everything the PROVISIONER needs to actually place the order with the
 * wholesaler/ISP — it is flattened onto the provisioning item's
 * `metadata.serviceDetails` so the Services & ISP panel can surface it.
 *
 * The `workflow` field is what gates provisioning behaviour:
 * - 'new_connection' → a fresh ISP order that must be placed BEFORE the install
 *   (NBN/Starlink/4G connection). Becomes a CRITICAL provisioning item (no
 *   specialWorkflow) so it counts toward scheduling readiness.
 * - 'porting'        → VoIP number port. CRITICAL service item
 *   (specialWorkflow: 'porting').
 * - 'activation'     → plan activation done AFTER the physical install
 *   (specialWorkflow: 'activation'); excluded from the pre-install critical set.
 */
export interface ReviewServiceDetails {
  /** Catalog service type — mirrors `ServicePlan['serviceType']`
   *  (e.g. 'NBN' | 'Starlink' | 'VoIP' | '4G Mobile' | '4G Data' | 'Other'). */
  serviceType: string;
  /** Mapped `DealCategory` value when the service type has a clean mapping
   *  (NBN/Starlink/VoIP). Used to keep `deal.selectedServices` consistent and
   *  to tag `metadata.serviceModule`. Absent for types without a 1:1 mapping. */
  dealCategory?: string;
  /** `service_plans/{id}` when picked from the catalog; absent for a custom/one-off plan. */
  planId?: string;
  /** Plan display name shown to the provisioner (e.g. "NBN Superfast 250/25"). */
  planName?: string;
  downloadSpeed?: string; // e.g. "250 Mbps"
  uploadSpeed?: string;   // e.g. "25 Mbps"
  dataAllowance?: string; // e.g. "Unlimited"
  /** Wholesaler / ISP the provisioner orders from (NBN Co, Starlink, Telstra…). */
  supplier?: string;
  /** Supplier's plan code / SKU for ordering. */
  supplierPlanCode?: string;
  /** What the provisioner needs to DO (see interface doc for behaviour). */
  workflow: 'new_connection' | 'porting' | 'activation';
  /** Existing service / account reference, when transferring or activating. */
  accountReference?: string;
  /** VoIP numbers to port (free text — comma/space separated). */
  numbersToPort?: string;
  /** Anything else the provisioner needs (service address, appointment prefs…). */
  orderNotes?: string;
}

export interface ReviewedQuoteItem {
  // Original quote item reference
  originalItemId: string; // ID from QuoteVersion.items array
  originalQuoteVersion: number; // Which quote version this came from

  // Product details (denormalized for history)
  name: string;
  category?: string;
  billingType: 'one-time' | 'recurring';

  // ISP / recurring-service ordering context (set via "Add Service" in Quote
  // Review). When present, the generated provisioning item is filed under
  // Services & ISP with the right workflow + this metadata for the provisioner.
  serviceDetails?: ReviewServiceDetails;

  // Original quantities and pricing
  originalQuantity: number;
  originalUnitPrice: number;
  originalTotalPrice: number;

  // Review decision
  approved: boolean; // Whether this item is approved for provisioning
  reviewNotes?: string; // Notes about why approved/rejected/modified

  // Hide from provisioning — keeps the item in deal value + billing,
  // but suppresses it from the provisioning_items collection (and therefore
  // from the tech job's plannedMaterials). Use for non-physical line items
  // like "Solution Install" labour from Better Proposals.
  hideFromProvisioning?: boolean;

  // Catalog provenance — set when the line was picked from the Parts Hub
  // (Add Line Items modal / quote review catalog add). Optional; historical
  // items never carry it.
  partsCatalogId?: string;

  // S5b (additive): the service_plans doc id a recurring line was picked
  // from — carried from the signed proposal by onProposalAccepted. Billing
  // matches the plan by this id first.
  servicePlanId?: string;

  // Revised values (if customer changed scope)
  revisedQuantity?: number; // If different from original
  revisedUnitPrice?: number; // If price was negotiated
  revisedTotalPrice?: number; // Calculated from revised values

  // Metadata
  reviewedAt: string; // ISO timestamp
  reviewedBy?: string; // User ID who approved/rejected
}

/**
 * Confirmed Service
 * Final service selection after quote review — determines which customer_services get created
 */
export interface ConfirmedService {
  serviceType: DealCategory; // e.g., STARLINK, NBN, SECURITY
  planName?: string; // Selected plan (for plan-based services)
  planId?: string; // Firestore plan ID
  billingType: 'one-time' | 'recurring';
  monthlyPrice?: number; // For recurring services
  installationPrice?: number; // For one-time services
  included: boolean; // Whether to create a customer_service for this
  source: 'original' | 'field_scope' | 'quote' | 'manual'; // How this service was identified
  notes?: string;
}

export interface ManualFlag {
  id: string;
  flaggedBy: string;
  flaggedByName: string;
  note: string;
  flaggedAt: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
}

/**
 * Office-set "provision-by-date" flag on a Deal. Used for cash-flow timing —
 * Casey or another office user marks a deal that must be ordered/ready by a
 * specific date. Bubbles the deal to the top of /home/provisioning when due
 * or overdue.
 */
export interface ProvisioningFlag {
  targetDate: string;          // YYYY-MM-DD — must be ordered/ready by this date
  reason: string;              // Free text — e.g. "cash flow", "supplier promo ends Friday"
  flaggedBy: string;           // userId
  flaggedByName: string;       // Display name (denormalized)
  flaggedAt: string;           // ISO timestamp
  updatedAt?: string;          // ISO timestamp of last edit
}

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  actor: string;
  actorName: string;
  action: string;
  details?: string;
  automated: boolean;
}

/**
 * Deal Note - Collaborative notes like Pipedrive
 * Freeform text notes with timestamps, authors, and @mentions
 */
export interface DealNote {
  id: string;
  content: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
  mentions?: {
    userId: string;
    userName: string;
  }[];
  pinned?: boolean;
}

// ============================================================================
// TASK QUEUE
// ============================================================================

export interface TaskItem {
  id?: string;
  tenantId: string;
  dealId: string;
  dealTitle: string;
  customerName: string;

  title: string;
  description?: string;
  taskType: 'stage_action' | 'follow_up' | 'colleague_request' | 'attention';

  assignedToRole: 'Sales' | 'Ops' | 'Provisioning' | 'Tech' | 'Admin';
  assignedToUserId?: string;
  assignedToUserName?: string;

  status: 'pending' | 'in_progress' | 'completed' | 'dismissed';
  completedAt?: string;
  completedBy?: string;

  priority: 'normal' | 'high' | 'urgent';

  triggerEvent: string;
  triggerStage?: string;
  linkedUrl?: string;

  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// QUOTE TEMPLATES
// ============================================================================

export interface QuoteTemplate {
  id?: string;
  tenantId: string;
  serviceType: DealCategory;
  variantName: string;
  variantCondition?: {
    field: string;
    value: unknown;
  };
  items: QuoteTemplateItem[];
}

export interface QuoteTemplateItem {
  name: string;
  defaultQuantity: number | 'varies';
  defaultUnitCost: number;
  defaultUnitPrice: number;
  billingType: 'one-time' | 'recurring';
  category?: string;
  sourceType?: 'template';
}

// ============================================================================
// FIRESTORE DOCUMENT INTERFACES
// ============================================================================

// V107.52.36 Phase 3 — `Customer` interface removed from this file.
// Import from '@/types' (canonical) instead. The schema.ts version is a
// strict superset of what was here (firstName/lastName/country/vault/
// addresses[]/etc.), so no caller loses fields.

/**
 * Collection: deals/{dealId}
 * Per-visit configuration for multi-visit install jobs (V107.52.30)
 * Created by the multi-visit builder modal, stored on deal.installPlan.visitPlans
 */
export interface VisitPlan {
  visitIndex: number;               // 0-based index
  name: string;                     // User-defined name (e.g., "First Fix — Cable Run")
  durationMinutes: number;          // Duration for this specific visit
  preferredTechIds: string[];       // Techs for this visit
  preferredTechNames: string[];     // Denormalized tech names
  allocatedItemIds: string[];       // IDs of scope items allocated to this visit
  allocatedItemNames: string[];     // Denormalized item names for display

  // === Tech Project Management additions (V107.53) ===
  // Populated during the PM Meeting workspace by Reuben + another tech.
  // Once the PM meeting is completed, these flow onto the corresponding
  // install Job document (installChecklist + plannedMaterials) so the
  // tech doing the install has a tailored kit + checklist per visit.
  checklistGroups?: PMVisitChecklistGroup[]; // Named checklist sections for this visit (e.g. "Pre-arrival", "On-site", "Wrap-up")
  /** @deprecated Use checklistGroups. Single flat list kept for backward compat. */
  checklist?: PMVisitChecklistItem[];        // Pre-install checklist (flat — V107.53.0 only)
  equipment?: PMVisitEquipmentItem[];        // Equipment / kit for this visit
  notes?: string;                            // Free-text PM notes for this visit
}

/**
 * A named group of checklist items inside a single visit. Lets the PM
 * meeting break the install into clear sections (e.g. "Pre-arrival prep",
 * "On-site work", "Wrap-up") so the tech reading the install job sees
 * structured guidance instead of a flat blob of tasks.
 *
 * Each group can also have a time + equipment allocation — a slice of the
 * parent visit's duration / equipment kit assigned to this specific task.
 * Surfaces as e.g. "Install AP · 1hr · uses: AP, Roof Mount" on the install
 * job's checklist when the PM meeting is completed.
 */
export interface PMVisitChecklistGroup {
  id: string;                          // crypto.randomUUID()
  name: string;                        // e.g. "Install AP"
  items: PMVisitChecklistItem[];
  // V107.53.2 — per-group time + equipment allocations
  allocatedMinutes?: number;           // Slice of the parent visit's duration in minutes
  allocatedEquipmentIds?: string[];    // IDs of PMVisitEquipmentItem.id belonging to the parent visit
}

/**
 * Single checklist line built during a PM meeting for one install visit.
 * Mirrors `InstallChecklistItem` shape so it can be lifted directly onto a
 * Job's installChecklist when the PM meeting completes.
 */
export interface PMVisitChecklistItem {
  id: string;                       // crypto.randomUUID()
  label: string;                    // e.g. "Install AP in living room"
  type?: 'checkbox' | 'text';
  notes?: string;                   // Optional context for the tech
}

/**
 * Single equipment / kit line built during a PM meeting for one install visit.
 * Mirrors `Material` shape so it can be lifted directly onto a Job's
 * plannedMaterials when the PM meeting completes.
 */
export interface PMVisitEquipmentItem {
  id: string;                       // crypto.randomUUID()
  name: string;                     // e.g. "UniFi U7 Pro Access Point"
  qty: number;                      // Quantity required for this visit
  category?: string;                // Optional grouping (e.g. "Networking")
  partsCatalogId?: string;          // Link to parts_catalog if picked from library
  notes?: string;
}

/**
 * PM Meeting status — tracks the lifecycle of a project-management meeting
 * created when the office flags "Needs a PM meeting first" at WON.
 * The PM meeting itself is a `type: 'Task'` Job with `isPMMeeting: true`.
 *
 * - 'pending'    — PM meeting Task created, sitting in unscheduled queue
 * - 'scheduled'  — PM meeting Task has a date/time
 * - 'completed'  — meeting run; install jobs created from the techs' plan
 *                  (or, legacy path, checklists + equipment applied to
 *                  already-existing install jobs)
 * - 'skipped'    — office/techs decided not to run it; normal scheduling resumes
 */
export type PMMeetingStatus = 'pending' | 'scheduled' | 'completed' | 'skipped';

/**
 * Office rough sizing captured when a PM meeting is flagged at WON — enough
 * for the scheduling team to pencil a tentative hold before the meeting sets
 * the real plan. Every sizing field is optional (the office can genuinely
 * not know yet). See Deal.pmTentativeEstimate for lifecycle notes.
 */
export interface PMTentativeEstimate {
  /** Rough on-site days (whole days; multi-day jobs are the point of this). */
  totalDays?: number;
  /** Rough crew size. */
  techCount?: number;
  /** Free-text target window, e.g. "mid-September" or "after the 20th". */
  targetWindow?: string;
  notes?: string;
  capturedAt: string;   // ISO string
  capturedBy: string;   // userId
  capturedByName?: string;
}

/**
 * A job photo synced onto the deal install summary on completion.
 * Mirrors the relevant fields of schema.ts `JobPhoto` (incl. the tech's
 * before/after/etc category tag) for display on the deal timeline + report.
 */
export interface SyncedInstallPhoto {
  url: string;
  caption?: string;
  takenAt?: string;
  takenBy?: string;
  category?: string;
}

/**
 * Deal/opportunity tracking the full workflow from intake to completion
 *
 * Relationships (stored as string IDs):
 * - customerId: Reference to customers/{customerId}
 * - linkedProjectId: Reference to projects/{projectId}
 */
export interface Deal {
  id?: string; // Firestore doc ID (set after creation)
  customerId: string; // Foreign key reference to Customer
  customerName?: string; // Denormalized customer name for display

  // === Contact split (V107.52.32) — resolved from customer's primary contacts at creation,
  // overridable per-deal. When the billable customer is a child of a parent company, the
  // account contact may legitimately live on the parent — callers can set that here.
  accountContactId?: string;
  accountContactName?: string;
  accountContactPhone?: string;
  accountContactEmail?: string;
  siteContactId?: string;
  siteContactName?: string;
  siteContactPhone?: string;
  siteContactEmail?: string;

  title: string;
  category?: DealCategory;
  complexity?: DealComplexity;
  jobType?: JobType; // Type of job (Presite, Regular, Service Call)
  starlinkKit?: StarlinkKitOption; // Starlink kit option: Techery-supplied or BYO
  stage: DealStage;
  proposalTemplateId?: string; // Better Proposals template ID
  /**
   * Per-proposal overrides on top of the picked template. Captures the
   * diffs Casey makes in the builder (section toggles, inline content
   * edits, T&Cs clause picks) without forking the master template.
   * The renderer merges these onto the template at context-build time;
   * `createProposalFromQuote` bakes them into the snapshot at send.
   */
  proposalTemplateOverrides?: import('./proposal.types').ProposalTemplateOverrides;
  /**
   * Deal value — stored INC-GST (the customer-facing figure used by the
   * BillingPage hero, refund/partial badges, and billing reports).
   */
  value?: number;
  /**
   * Ex-GST companion to `value`, stamped alongside it on the GST-grossing
   * write paths (BP webhook `onProposalSigned`, BP scanner
   * `finaliseProposalMapping`). Lets billing/audit recover the pre-GST base
   * without re-deriving it. Absent on older deals.
   */
  valueExGst?: number;
  notes?: string;

  // === Service Module System ===
  selectedServices?: DealCategory[];
  serviceModules?: {
    [key in DealCategory]?: ServiceModuleData;
  };

  // === Intake notes seeding (idempotency) ===
  // Set true once the deal's per-service intake details (Surveillance camera
  // info, Starlink kit/plan, VoIP handsets, each service's "Notes" box, …)
  // have been mirrored into the unified `notes` collection. That mirror is
  // what makes intake surface on the deal timeline, the scheduler side panel,
  // and the tech's field view — surfaces that read the `notes` collection,
  // not `serviceModules`. Claimed transactionally in seedDealIntakeNotes() so
  // the mirror runs exactly once even if multiple paths trigger it.
  intakeNotesSeeded?: boolean;
  intakeNotesSeededAt?: string; // ISO timestamp

  // === Workflow Tracking ===
  stageHistory?: StageHistoryEntry[];
  presiteRequired?: boolean;
  presiteOverridden?: boolean;
  presiteOverriddenBy?: string;

  // === Quoting ===
  quoteVersions?: QuoteVersion[];
  activeQuoteVersion?: number;

  // === Scoped Quoting (V107.52.12+) ===
  quoteScopes?: QuoteScope[];
  scopeSummary?: string;
  pandaDocPayload?: PandaDocScopedPayload;

  // === Proposal Builder V2 (additive — docs/proposal-v2/S1.md) ===
  /**
   * The V2 proposal narrative (hero, options, sections…). Money stays in
   * `quoteScopes`; this is saved in the same CAS write as quoteScopes.
   */
  proposalV2?: ProposalV2Content;
  /**
   * 2 once the deal has been opened in Proposal Builder V2 — from then on
   * the V2 builder is its only entry (V1 would drop the V2 fields on save).
   * Absent on every other deal.
   */
  proposalBuilderVersion?: 2;

  /**
   * Optional proposal-wide percentage discount (0–100) the office takes off
   * the ONE-TIME (labour + items) subtotal. The customer sees it on the
   * proposal (Subtotal → Discount → Total) and is invoiced the discounted
   * amount — the server invoice applies it too. Monthly recurring plans are
   * NOT discounted. Absent / 0 → no discount (byte-identical pricing).
   * Always run through `sanitizeDiscountPercent` at the money boundary.
   */
  quoteDiscountPercent?: number;

  // === Quote Review (WON stage gate) ===
  quoteReviewStatus?: 'pending' | 'in_progress' | 'completed';
  quoteReviewedItems?: ReviewedQuoteItem[]; // Approved/rejected items from quote
  quoteReviewedBy?: string; // User ID who reviewed
  quoteReviewedByName?: string; // User name for display
  quoteReviewedAt?: string; // ISO timestamp
  quoteReviewNotes?: string; // General notes about the review

  // === Service Confirmation (post-review gate) ===
  serviceConfirmationStatus?: 'pending' | 'in_progress' | 'completed';
  confirmedServices?: ConfirmedService[]; // Final service selections after review
  serviceConfirmedBy?: string; // User ID who confirmed
  serviceConfirmedByName?: string; // User name for display
  serviceConfirmedAt?: string; // ISO timestamp
  serviceConfirmationNotes?: string; // Notes about service changes

  // === Needs Attention ===
  needsAttention?: boolean;
  needsAttentionReasons?: string[];
  needsAttentionSeverity?: 'yellow' | 'red';
  manualFlags?: ManualFlag[];

  // === Awaiting Funds (V107.52.30+) ===
  awaitingFunds?: boolean;                     // True when any provisioning item is AWAITING_FUNDS
  awaitingFundsAmount?: number;                // Total dollar amount required
  awaitingFundsNotes?: string;                 // Notes about what the funding is for
  awaitingFundsItemIds?: string[];             // Provisioning item IDs awaiting funds
  awaitingFundsAt?: string;                    // ISO timestamp when flagged
  awaitingFundsBy?: string;                    // userId who flagged
  awaitingFundsByName?: string;                // userName who flagged
  fundingReceivedAt?: string;                  // ISO timestamp when funds confirmed
  fundingReceivedBy?: string;                  // userId who confirmed

  // === Held — Awaiting Customer Deposit ===
  heldAwaitingDeposit?: boolean;               // True when any provisioning item is HELD_AWAITING_DEPOSIT
  heldDepositAmount?: number;                  // Required deposit amount (AUD)
  heldDepositNotes?: string;                   // Notes about the deposit
  heldDepositItemIds?: string[];               // Provisioning item IDs held
  heldDepositAt?: string;                      // ISO timestamp when flagged
  heldDepositBy?: string;                      // userId who flagged
  heldDepositByName?: string;                  // userName who flagged
  heldDepositReceivedAt?: string;              // ISO timestamp when deposit confirmed
  heldDepositReceivedBy?: string;              // userId who confirmed

  // === Provisioning Flag (V107.52.36+) — manual "must be ordered/provisioned by date X" ===
  // Set by office staff (e.g. Casey) when a deal needs to be provisioned on a
  // specific day for cash-flow / supplier-timing reasons. Surfaces as an amber
  // pill on the deal card and bubbles to the top of the list when due/overdue.
  provisioningFlag?: ProvisioningFlag;

  // === Next Action ===
  nextAction?: string;
  nextActionAssignee?: string;

  // === Project Link ===
  linkedProjectId?: string;

  // === Install Plan (captured at WON → PROVISIONING) ===
  installPlan?: {
    visitCount: number;             // Number of planned visits (1, 2, 3, etc.)
    hoursPerVisit: number;          // Estimated hours per visit
    customerPreferredDate?: string; // YYYY-MM-DD preferred start date
    notes?: string;                 // Special instructions for provisioning/scheduling
    capturedAt: string;             // ISO timestamp
    capturedBy: string;             // userId
    visitPlans?: VisitPlan[];       // Per-visit configuration from multi-visit builder (V107.52.30)
    quotedLaborHours?: number;      // Labour hours quoted across the deal's scopes — pre-fills duration
    quotedTechCount?: number;       // Crew size quoted (max techs any scope needs)
  };

  // Set by the `onProvisioningItemUpdated` Cloud Function when ALL provisioning
  // items are ready (SHIPPED/AT_OFFICE/DELIVERED) but the deal has no
  // `installPlan` yet — i.e. the scheduling modal ("how many techs / how many
  // visits") was never completed. The deal is held at PROVISIONING instead of
  // auto-advancing with defaults; the office completes the modal via the deal's
  // "Ready to Schedule" hero, which clears this and advances to SCHEDULING.
  provisioningReadyAwaitingPlan?: boolean;

  // === Tech Project Management Meeting (V107.53; reworked 2026-08) ===
  // A `type: 'Task'` Job dropped into the unscheduled queue (linked by
  // pmMeetingJobId). Since the 2026-08 workflow change the meeting is raised
  // AFTER the deal is scheduled + provisioned (from the deal's Actions menu,
  // the PMMeetingCard prompt at SCHEDULING, or the schedule's job panel) —
  // the office plans + schedules first, and the meeting reviews that plan.
  // Completing it decorates the existing install jobs with each visit's
  // checklists/equipment; it no longer owns scheduling or clears installPlan.
  // NOTE: the quote-review page / pre-site briefing can set pmMeetingRequired
  // as an ADVISORY flag (no pmMeetingJobId) — that cues the PMMeetingCard
  // "raise it now" prompt once the deal reaches SCHEDULING.
  pmMeetingRequired?: boolean;       // True once a PM was flagged (see NOTE)
  pmMeetingJobId?: string;           // jobs/{jobId} of the PM meeting Task
  /** Display label of the CURRENT meeting Task (mirrors the job's
   *  `meetingLabel`) — "Toolbox talk", "Team meeting", etc. Absent on
   *  pre-2026-08 meetings; render as "PM Meeting" then. */
  pmMeetingLabel?: string;
  pmMeetingStatus?: PMMeetingStatus;
  pmMeetingCreatedAt?: string;
  pmMeetingCreatedBy?: string;
  /**
   * Mid-meeting draft of the techs' visit plans (V107.53). Written by
   * updatePMVisitPlans (the PM workspace's "Save draft" + the pre-complete
   * flush) — deliberately NOT stored on `installPlan`, because the gear-ready
   * Cloud Function treats installPlan presence as "scheduling details are
   * final" and would auto-create install jobs from a half-finished draft.
   * completePMMeeting lifts this into the real installPlan and clears it.
   */
  pmMeetingDraftVisitPlans?: VisitPlan[];
  /**
   * Boolean convenience flag mirroring `pmMeetingStatus === 'completed'`.
   * Kept because earlier code (deal.service.ts, MissionControlCard) writes /
   * reads this directly — `pmMeetingStatus` superseded it but the boolean
   * sticks around for backward compatibility.
   */
  pmMeetingCompleted?: boolean;
  pmMeetingCompletedAt?: string;     // ISO string (NOT Firestore Timestamp — see CLAUDE.md)
  pmMeetingCompletedBy?: string;
  pmMeetingCompletedByName?: string;
  /** Free-text notes captured at PM meeting completion (deal.service.ts path). */
  pmMeetingNotes?: string;
  /**
   * Office rough sizing captured when flagging "Needs a PM meeting first" at
   * WON (2026-08 UX audit F7), so the scheduling team can pencil a tentative
   * hold (e.g. a `tentative_job` ScheduleEvent) while the deal waits at
   * PROVISIONING for the meeting. This is the OFFICE's pre-meeting guess;
   * `pmMeetingDraftVisitPlans` is the TECHS' mid-meeting draft — different
   * authors, different lifecycles. Deliberately NOT stored on `installPlan`:
   * the gear-ready Cloud Function treats installPlan presence as "scheduling
   * details are final" and would book default install jobs from it. Cleared
   * by completePMMeeting (real plan supersedes it) and skipPMMeeting (stale).
   */
  pmTentativeEstimate?: PMTentativeEstimate;

  // === Contact Confirmation (SCHEDULING stage — customer appointment confirmation) ===
  contactStatus?: 'not_contacted' | 'attempted' | 'confirmed';
  contactStatusAt?: string; // ISO timestamp of last status change
  contactStatusBy?: string; // userId who last updated
  contactStatusByName?: string; // userName (denormalized)

  // === Job Links ===
  createdJobId?: string; // Link to jobs/{jobId} for pre-site job
  installJobId?: string; // Link to jobs/{jobId} for most recent install job (backward compat)
  installJobIds?: string[]; // ALL install job IDs for this deal (supports multi-visit)
  /**
   * Idempotency marker for the SCHEDULING-entry visit loop. Stamped after the
   * visit loop in moveDealToStage successfully creates install jobs from
   * installPlan.visitCount, and cleared by moveDealToStage on any transition
   * to a non-SCHEDULING stage. Lets a duplicate moveDealToStage(SCHEDULING)
   * call (race, double-click, replay) skip the loop instead of creating
   * (visitCount - 1) duplicate jobs. See AUDIT_NOTES.md HIGH-#H2.
   */
  installJobsCreatedAt?: string; // ISO timestamp

  /**
   * Recorded when the SCHEDULING-entry visit loop fails to create one or
   * more visit jobs. Set by moveDealToStage and cleared on a clean re-run
   * that creates the missing visits. Lets ops see partial-failure state
   * (e.g. "visit 2 failed, visits 1 + 3 succeeded") so they can retry.
   * See AUDIT_NOTES.md CRITICAL-#C5.
   */
  installJobsCreationError?: {
    at: string;            // ISO timestamp of the failing attempt
    failedVisits: number[]; // visitNumbers that threw in the last loop run
    message: string;       // Joined error messages, one per failed visit
  };

  /**
   * Captures the most recent per-stage side-effect failure during a
   * moveDealToStage transition. Stage write happens BEFORE side effects
   * (pre-site job creation, provisioning generation, etc.) so a side-
   * effect failure leaves the deal in the new stage with missing
   * downstream entities. This field surfaces the gap so ops can see and
   * remediate. Cleared on every stage transition start; re-written by the
   * specific catch block that fails. See AUDIT_NOTES.md CRITICAL-#C6 and
   * MEDIUM-#M1.
   */
  stageTransitionError?: {
    at: string;     // ISO timestamp
    stage: string;  // The DealStage value that was being entered
    source: string; // Which side effect failed: 'presite-job-creation' |
                    // 'provisioning-generation' | 'install-jobs-loop' | etc.
    message: string;
  };

  // === Scope More Time Request (synced from pre-site job) ===
  // Tech flagged at pre-site wrap-up that they need more time to finish the
  // scope (write-up, design plans, Reuben review, etc). Office sees a sidebar
  // card with a "Schedule scope time" hero button that drops a Task job into
  // the unscheduled queue.
  scopeMoreTimeRequest?: {
    timeNeeded: string;         // '30min' | '1hr' | '2hr' | 'half-day' | 'full-day'
    timeNeededMinutes: number;  // Numeric for scheduling
    needsDesignPlans: boolean;
    wantsReubenAssist: boolean;
    notes?: string;
    requestedAt: string;        // ISO timestamp
    requestedBy: string;        // techId
    requestedByName?: string;   // Denormalized for display
    fromJobId: string;          // The pre-site job that flagged it
    scheduled?: boolean;        // True once office has scheduled the follow-up
    scheduledJobId?: string;    // The Task job created when scheduled
    scheduledAt?: string;       // ISO timestamp
  };

  // === Return Visit (synced from job on NEEDS_RETURN_VISIT) ===
  returnVisitRequired?: boolean;
  returnVisitReason?: string;
  returnVisitNotes?: string;
  returnVisitStatus?: 'ready_to_schedule' | 'blocked'; // ready = follow-up job created, blocked = waiting on resolution
  returnVisitJobId?: string; // Original job that flagged the return
  returnVisitFollowUpJobId?: string; // Auto-created follow-up job (only if ready_to_schedule)
  returnVisitFlaggedAt?: string; // ISO timestamp
  returnVisitFlaggedBy?: string; // techId
  returnVisitTechName?: string; // Denormalized for display

  // === Install Completion (synced from job on COMPLETED) ===
  installCompletedAt?: string; // ISO timestamp (last completed job)
  installCompletedBy?: string; // techId (last completed job)
  installTechName?: string; // Denormalized for display (last completed job)
  installDuration?: number; // Actual on-site minutes (last completed job)
  installSummary?: {
    customerSummary?: string; // AI-generated customer-facing summary
    technicalReport?: string; // AI-generated technical report
    completionNote?: string; // Tech's wrap-up note
    fieldLogCount?: number;
    photoCount?: number;
    checklistCompleted?: boolean;
    photos?: SyncedInstallPhoto[]; // Synced from job.photos[] on completion
    additionalGearUsed?: { what?: string; qty?: number; reason?: string }[]; // On-spot extras from the tech
    usedMaterials?: { name: string; quantity: number; unit?: string; sku?: string; addedByTech?: boolean }[]; // What the tech confirmed using
    completionReasoning?: Record<string, unknown>; // Tech's JobCompletionReasoning (when captured)
  };
  // Per-job summaries for multi-visit deals (keyed by jobId)
  installSummaries?: Record<string, {
    jobId: string;
    techName?: string;
    duration?: number;
    completedAt?: string;
    customerSummary?: string;
    technicalReport?: string;
    completionNote?: string;
    fieldLogCount?: number;
    photoCount?: number;
    checklistCompleted?: boolean;
    photos?: SyncedInstallPhoto[]; // Synced from job.photos[] on completion
    additionalGearUsed?: { what?: string; qty?: number; reason?: string }[];
    usedMaterials?: { name: string; quantity: number; unit?: string; sku?: string; addedByTech?: boolean }[];
    completionReasoning?: Record<string, unknown>;
  }>;

  // === Pre-site completion (synced from pre-site job by job-side-effects) ===
  presiteCompletionNote?: string; // Tech's pre-site wrap-up note (goes to sales/quoting)
  presiteCompletedAt?: string;    // ISO timestamp
  presiteCompletedBy?: string;    // techId
  presiteFieldLogCount?: number;

  // === Install-tech briefing ===
  // The ONE human-authored "what the install techs should do on site" note.
  // Written by the pre-site tech at pre-site completion, or by the office
  // (deal workspace TechBriefingCard) for deals that skip pre-site. Surfaced
  // to techs in the V6 job modal's FieldBriefingCard. See TechBriefing.
  techBriefing?: import('@/types/schema').TechBriefing;

  // === Field Scope (synced from pre-site job on submission) ===
  fieldScope?: import('@/types/schema').FieldScope;        // Legacy single-scope (mirrored from fieldScopes[0] for backward compat)
  fieldScopes?: import('@/types/schema').FieldScope[];     // Multi-scope support — one pre-site can cover multiple work packages
  fieldScopeJobId?: string; // jobs/{jobId} that submitted the scope
  fieldScopeSubmittedAt?: string; // ISO timestamp
  fieldScopeSubmittedBy?: string; // techId

  // AI scope matcher cache (V107.53). When the proposal builder
  // first sees a submitted field scope with no existing quote
  // scopes, it runs `matchFieldScopeToCatalog` in the background
  // and persists the resulting `ScopeMatchResult[]` here. The
  // `FieldScopeReviewDrawer` then reads from this cache for
  // instant rendering on subsequent opens — no second Claude
  // round-trip. Cleared / refreshed when the tech re-submits a
  // newer field scope (compare `matchesAt` vs `submittedAt`).
  fieldScopeMatches?: import('@/services/ai-scope-matcher.service').ScopeMatchResult[];
  fieldScopeMatchesAt?: string;
  fieldScopeMatchesMethod?: 'ai' | 'fuzzy';
  // Service plans detected by `addServicePlansToQuote` at the same
  // time the matcher ran. Cached alongside the matches so the
  // drawer can show them and the auto-apply step can land them
  // into the "Service Plans" scope without re-running the lookup.
  fieldScopeServicePlans?: import('@/types/schema').PlaygroundQuoteItem[];
  // Set when the matcher's output was auto-applied to
  // `quoteScopes` on this deal. Used to (a) avoid double-applying
  // on subsequent loads and (b) trigger the first-time
  // auto-open of the review drawer.
  fieldScopeAutoAppliedAt?: string;

  // === Billing (tracked at BILLING stage) ===
  // 'bad_debtor' is a manual office override for invoiced deals that
  // aren't getting paid. Deal stays at BILLING stage — does not advance
  // to COMPLETED — so it remains visible on /home/billing under its own tab.
  // 'partially_paid' is P2.0.8 deposit-invoice state — deposit milestone
  // has cleared via Stripe but the final milestone is still due. Mirrored
  // to the deal by the webhook so the BillingPage card surfaces a
  // "Deposit paid" badge instead of staying as plain "Awaiting Payment".
  billingStatus?: 'to_bill' | 'billed' | 'partially_paid' | 'paid' | 'bad_debtor';
  billedAt?: string;          // ISO timestamp when invoice was sent
  billedBy?: string;          // userId
  billedByName?: string;
  invoiceRef?: string;        // External invoice reference (Utilmate, Stripe, etc.)
  billingSummary?: string;    // AI-generated short customer billing summary
  paidAt?: string;            // ISO timestamp when payment received
  paidBy?: string;            // userId who confirmed payment
  paidByName?: string;
  // Bad debtor metadata — set when office flags an invoice as unrecoverable
  badDebtorReason?: string;   // Free-text reason ("3+ unpaid invoices", "disputed", etc.)
  badDebtorFlaggedAt?: string;
  badDebtorFlaggedBy?: string;
  badDebtorFlaggedByName?: string;
  // Override flag — set when the office bills a deal but explicitly
  // skips the customer-services creation step (e.g. upgrade to an
  // existing service, one-off install, already-tracked service).
  // Reason is captured to the deal's activity log too.
  servicesSkippedAtBilling?: boolean;
  servicesSkippedReason?: string;

  // === Review request (fires when billingStatus → 'billed') ===
  // Set server-side by handleDealBillingStatusChange (onDealUpdated). The
  // customer is enrolled in the Flodesk Google Review workflow AND sent a
  // review-request SMS via MessageMedia. `reviewRequestSentAt` is the
  // once-per-deal idempotency stamp for the whole event. The reviewRequestSms*
  // fields are the SMS rail's audit trail (status: 'sent' | 'failed' |
  // 'skipped'; skipped reasons: no_review_url / no_valid_phone). SMS is
  // dormant until MessageMedia creds land.
  reviewRequestSentAt?: string;        // ISO — whole review event handled once
  reviewRequestSmsStatus?: 'sent' | 'failed' | 'skipped';
  reviewRequestSmsSentAt?: string;     // ISO
  reviewRequestSmsTo?: string;         // E.164 destination
  reviewRequestSmsMessageId?: string;  // MessageMedia message_id
  reviewRequestSmsError?: string;      // errorCode: message, or skip reason

  // === Xero Integration (V107.52.37+) — populated on first successful push.
  // See docs/XERO_INTEGRATION_PLAN.md §4.3 and src/types/xero.types.ts.
  xeroInvoiceId?: string;       // Xero InvoiceID (UUID) — stable forever
  xeroInvoiceNumber?: string;   // e.g. "INV-2026-0042"
  xeroInvoiceUrl?: string;      // Deep link to view in Xero
  xeroInvoiceStatus?: 'DRAFT' | 'SUBMITTED' | 'AUTHORISED' | 'PAID' | 'VOIDED' | 'DELETED';
  xeroLastSyncedAt?: string;    // ISO timestamp of most recent push/status sync
  xeroPushError?: string;       // Last user-friendly error if push failed (cleared on success)

  // === Stripe payment + refund (P2.0.5 / P2.0.7) ===
  /** Stripe Charge ID once the customer's card has cleared. Mirrors
   *  TheoInvoice.stripeChargeId — set when the office wants to refund
   *  from the BillingPage card without a callable round-trip to load
   *  the TheoInvoice doc. */
  stripeChargeId?: string;
  /** ISO timestamp of refund. Drives the "Refunded" pill on paid deal
   *  cards. Stamped by handleChargeRefunded (P2.0.7). */
  refundedAt?: string;
  /** Refund amount in DOLLARS. Full or partial. */
  refundAmount?: number;
  /** Stripe Refund ID — audit cross-reference. */
  stripeRefundId?: string;

  // === Activity Log ===
  activityLog?: ActivityLogEntry[];

  // === Notes (Collaborative notes like Pipedrive) ===
  dealNotes?: DealNote[];

  // === Deal Owner ===
  dealOwnerId?: string;
  dealOwnerName?: string;

  // Job Scheduling & Location (from legacy system)
  jobStatus?: JobStatus; // Current job status
  jobAddress?: string; // Job site address (can differ from customer address)
  jobCity?: string;
  jobState?: string;
  jobZipCode?: string;
  lat?: number; // Latitude for mapping
  lng?: number; // Longitude for mapping
  scheduledDate?: string; // Date in YYYY-MM-DD format
  scheduledTime?: string; // Time in HH:MM format
  duration?: number; // Expected duration in minutes

  // Job Contact Information
  // NOTE: Deals created before V107.52.29 may have contactName/contactPhone
  // but NOT contactEmail (it wasn't denormalized). Any code that reads
  // contactEmail should fall back to fetching customer.email via customerId
  // if contactEmail is empty. The stage-dispatcher Cloud Function does this
  // automatically and backfills the deal doc on first access.
  contactName?: string; // On-site contact name
  contactPhone?: string; // On-site contact phone
  contactEmail?: string; // On-site contact email

  // Critical Site Access Information
  criticalNotes?: string; // Gate codes, access instructions, special warnings
  gateCode?: string; // Specific gate code field
  siteAccessInstructions?: string; // How to access the site

  // Job Details & Scope
  jobDescription?: string; // Detailed job description
  scope?: string; // Project scope
  services?: string[]; // Array of services to be performed
  priority?: TechRequestPriority; // Job priority

  // Assignment & Team
  assignedTo?: string; // Assigned technician ID
  assignedToName?: string; // Assigned technician name
  assignedTechIds?: string[]; // Array of technician IDs (multi-tech support)

  // Timestamps for job lifecycle
  travelStartedAt?: Timestamp; // When tech starts traveling
  jobStartedAt?: Timestamp; // When actual work begins
  pausedAt?: Timestamp; // When job is paused
  completedAt?: Timestamp; // When job is completed
  actualDuration?: number; // Actual duration in minutes

  // PM Meeting Workflow fields are declared in the canonical block earlier
  // in this interface (see V107.53 section). Keep this comment as a marker
  // so future merges don't reintroduce a duplicate set of pmMeeting* fields
  // here — the earlier block uses ISO strings (per CLAUDE.md) and includes
  // the full lifecycle (pmMeetingStatus, pmMeetingJobId, etc.).

  // Standard timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string; // User ID
  createdByName?: string; // Denormalised creator name for display
  wonAt?: Timestamp;
  lostAt?: Timestamp;

  // Multi-tenancy
  tenantId?: string;

  // Better Proposals API Integration
  betterProposalsId?: string;        // Proposal ID from Better Proposals API
  proposalUrl?: string;              // Better Proposals viewer/editor URL (from API: ProposalView)
  proposalCreatedAt?: Timestamp;     // When draft proposal was created in BP
  proposalSentAt?: Timestamp;        // When proposal was sent to customer
  proposalSignedAt?: Timestamp;      // When customer signed the proposal
  proposalSignedBy?: string;         // Name of person who signed
  proposalSignedEmail?: string;      // Email of person who signed
  proposalAcceptedItems?: ProposalAcceptedItem[]; // Line items from signed proposal
  monthlyRecurring?: number | null;  // Monthly recurring total from signed proposal

  // ── Native proposals (Phase 5+ — see docs/NATIVE_PROPOSALS_ARCHITECTURE.md §4.6) ──
  // Lives alongside the BP fields above during the parallel-run window.
  // Phase 6 cutover removes the BP fields; nativeProposal* persist.
  /** FK → proposals/{proposalId}. Set when the deal has a native proposal record. */
  nativeProposalId?: string;
  /** Public viewer URL — built via buildPublicProposalUrl(). Convenience for the workspace card. */
  nativeProposalUrl?: string;
  /** Denormalised status for list views — full state lives on proposals/{id}.status. */
  nativeProposalStatus?:
    | 'draft'
    | 'sent'
    | 'viewed'
    | 'accepted'
    | 'declined'
    | 'expired'
    | 'withdrawn';
  /** Server timestamp set when status flipped to 'sent'. */
  nativeProposalSentAt?: string | Timestamp;
  /** Server timestamp set when status flipped to 'accepted'. */
  nativeProposalAcceptedAt?: string | Timestamp;
  /** Server timestamp set when a BP→native migration mirror was applied (Phase 4 tooling). */
  nativeProposalMigratedAt?: string | Timestamp;

  // Better Proposals PDF Integration (temporary until PandaDoc)
  proposalIntro?: string;            // Extracted intro text from first quote PDF
  proposalPDFs?: ProposalPDF[];

  // Attachments — photos and files attached to this deal, or propagated
  // from linked jobs/faults. Populated via attachment.service.ts.
  attachments?: Attachment[];

  // === Provisioning ETA (V107.52.18+) ===
  provisioningETA?: string; // Expected date all provisioning will be ready (YYYY-MM-DD)

  // === Provisioning Cleared (manually cleared from provisioning queue) ===
  provisioningCleared?: boolean;          // True when manually dismissed from provisioning page
  provisioningClearedAt?: string;         // ISO timestamp
  provisioningClearedBy?: string;         // userId
  provisioningClearedByName?: string;     // userName

  // === Parallel Schedule & Provision (V107.52.22+) ===
  parallelProvisioning?: boolean; // True when deal was sent to scheduling before provisioning was complete
  parallelProvisioningAt?: string; // ISO timestamp when schedule & provision was triggered

  // === Deposit Invoice (P2.0.8 — V107.53+) ===
  /**
   * Set when a deposit invoice has been raised against this deal via
   * `sendInvoice({ milestone: 'deposit' })`. Used by `DealActionButtons`
   * to hide the "Raise Deposit Invoice" hero once raised, and by
   * `OfficeDashboardPage` attention surfaces to flag deposit-paid deals
   * still awaiting balance billing.
   *
   * The authoritative deposit state lives on the TheoInvoice doc at
   * `tenants/{tenantId}/theo_invoices/deal-{dealId}` — these fields
   * are denormalised for UI gating without subscribing to the invoice.
   */
  depositInvoiceRaisedAt?: string;       // ISO timestamp when deposit was raised
  depositInvoiceRaisedBy?: string;       // userId who triggered the raise
  depositAmount?: number;                // Deposit amount in dollars, inc-GST
  /** TheoInvoice ID — deterministic `deal-{dealId}`. */
  depositInvoiceId?: string;
  /** Set true when the deposit milestone has been paid by the customer
   *  (Stripe webhook flipped the milestone to 'paid'). Phase C wires this. */
  depositInvoicePaidAt?: string;
  /** ISO timestamp when the FINAL-milestone payment email was sent to
   *  the customer (P2.0.8 Phase F manual trigger). Drives the
   *  "Send Final Payment Email" button's visibility on the deal
   *  workspace — once stamped, the button hides. */
  depositFinalEmailSentAt?: string;
  depositFinalEmailMessageId?: string;
  /** Set by cancelInvoice (Phase E) when the deposit invoice is voided. */
  depositInvoiceCancelledAt?: string;
  depositInvoiceCancelledReason?: string;

  // === Soft Delete ===
  deleted?: boolean;          // Soft-delete flag — filtered out of queries when true
  deletedAt?: string;         // ISO timestamp
  deletedBy?: string;         // userId who deleted
  deletedByName?: string;     // userName who deleted
  deletedReason?: string;     // Optional reason for deletion
}

/**
 * Better Proposals PDF Upload
 * Tracks uploaded proposal PDFs at each deal stage with AI-extracted content
 */
export interface ProposalPDF {
  id: string;
  type: 'quote' | 'variation' | 'signed';
  fileName: string;
  pdfUrl: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName: string;

  // AI extraction
  extractionStatus: 'pending' | 'processing' | 'completed' | 'failed';
  extractedAt?: string;
  extractionError?: string;

  // Extracted content
  extractedIntro?: string;
  extractedItems?: ProposalExtractedItem[];
  extractedTotals?: {
    subtotal?: number;
    gst?: number;
    total?: number;
    monthlyTotal?: number;
  };

  // Comparison (for signed PDFs vs original quote)
  comparisonResult?: {
    matchedItems: number;
    changedItems: number;
    newItems: number;
    removedItems: number;
    changes: ProposalChange[];
  };
}

export interface ProposalExtractedItem {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  category?: string;
  billingType?: 'one-time' | 'recurring';
  sku?: string;
}

export interface ProposalChange {
  itemName: string;
  changeType: 'added' | 'removed' | 'quantity_changed' | 'price_changed';
  originalValue?: string;
  newValue?: string;
}

/**
 * Line item from a signed Better Proposals proposal.
 * Stored on deal.proposalAcceptedItems[] when the onProposalSigned webhook fires.
 */
export interface ProposalAcceptedItem {
  tableTitle: string;      // Price table name (e.g., "Your Recommended Products & Services")
  label: string;           // Item name
  description: string;     // Item description
  unitCost: number;        // Per-unit cost (ex GST, parsed from BP string)
  quantity: number;        // Quantity
  cost: number;            // Total line cost (parsed from BP string)
  isOptional: boolean;     // Was this an optional line item?
  isSelected: boolean;     // Did customer select it? (always true for non-selectable items)
  recurringType: string;   // 'One Time Payment' | 'Monthly' | etc. (BP string values)
  monthlyCost: number;     // Monthly recurring cost (parsed from BP MonthlyCost string)
}

/**
 * Collection: provisioning_items/{itemId}
 * Equipment checklist items for won deals
 *
 * Relationships (stored as string IDs):
 * - dealId: Reference to deals/{dealId}
 */
export interface ProvisioningItem {
  id?: string; // Firestore doc ID (set after creation)
  // Required by Phase 0.5 Session A tenant-isolation rules. Every write
  // must carry the user's canonical tenantId — the rules will reject the
  // write otherwise. Was missing from the type before May 2026; that gap
  // hid several writers (provisioning.service.ts batch writes + two
  // fault.service.ts addDoc paths) that wrote without setting it. All
  // fixed in the same PR that added this field.
  tenantId: string;
  dealId: string; // Foreign key reference to Deal (or empty string for fault-sourced items)
  faultId?: string; // Foreign key reference to Fault (when provisioning parts for a fault)
  sourceType?: 'deal' | 'fault'; // Origin of the provisioning item (defaults to 'deal' for backward compat)
  itemName: string;
  status: ProvisioningItemStatus;
  quantity?: number;
  metadata?: Record<string, unknown>; // Flexible metadata object
  notes?: string;
  billingType?: 'one-time' | 'recurring';
  specialWorkflow?: 'porting' | 'activation' | 'kit_order';

  // === Category: hardware vs service (V107.52.30+) ===
  provisioningCategory?: 'hardware' | 'service'; // Explicit category — set on creation, can be overridden

  // === Per-service-type provisioning workflow (Services & ISP panel) ===
  // Only meaningful for service items. `serviceWorkflowType` is the canonical
  // key into SERVICE_PROVISIONING_WORKFLOWS ('NBN' | 'VoIP' | 'Starlink' |
  // 'DEFAULT'); `serviceStage` is the active stage id within that workflow.
  // The item's `status` is kept in sync with the stage's kind via
  // serviceStageKindToStatus so the deal's scheduling gate still works.
  serviceWorkflowType?: string;   // canonical workflow key
  serviceStage?: string;          // active stage id within the workflow
  serviceStageChangedAt?: string; // ISO timestamp of last stage change
  serviceStageBy?: string;        // userId who changed the stage
  serviceScheduledAt?: string;    // ISO — date/time captured at a requiresDateTime stage (e.g. VoIP port cutover)

  // === Starlink kit provisioning fields (V107.52.18+) ===
  starlinkEmail?: string; // Starlink account email — captured when ordering
  starlinkModel?: 'Standard' | 'Mini' | 'Gen 1' | 'Gen 2' | 'Gen 3'; // Starlink hardware model (Standard = Gen 3; Gen 1/2/3 kept for backward compat)
  starlinkAddress?: string; // Service address for the Starlink kit — captured when ordering
  starlinkKitNumber?: string; // Kit serial number — captured when received at office
  starlinkAccountNumber?: string; // Starlink account number (ACC-XXXXXXX-XXXXX-XX) — captured when received at office, propagated to customer_services.metadata.starlinkAccountNumber on activation
  /** Speed plan the kit is destined for ('100' | '200' | 'MAX'). Seeded from
   *  the deal's Starlink service plan at generation, editable on the card.
   *  Rendered via formatStarlinkKitLabel — see @shared/domain/starlink-kit. */
  starlinkSpeedTier?: StarlinkSpeedTier;
  /** True when the service is self-managed (customer's own Starlink account) —
   *  the "S/M" in STARLINK S/M KIT. */
  starlinkSelfManaged?: boolean;
  /** @deprecated No longer captured — kit number is the only physical identifier we track. Field kept for back-compat reads of legacy provisioning items. */
  starlinkRouterSerial?: string;
  /** @deprecated No longer captured — kit number is the only physical identifier we track. Field kept for back-compat reads of legacy provisioning items. */
  starlinkDishSerial?: string;
  /** @deprecated No longer captured — kit number is the only physical identifier we track. Field kept for back-compat reads of legacy provisioning items. */
  starlinkMountSerial?: string;

  // === Supplier & tracking (V107.52.18+) ===
  supplier?: string; // Supplier name from parts catalog (Leader, 4Cabling, Dicker Data, etc.)
  statusChangedAt?: string; // ISO timestamp of last status change
  orderedAt?: string; // ISO timestamp when marked as SHIPPED/ORDERED
  expectedDelivery?: string; // Expected delivery date YYYY-MM-DD
  trackingDetails?: string; // Free text captured at SHIPPED — courier + tracking number, or a tracking URL
  orderByDate?: string; // YYYY-MM-DD — deadline to have this item ordered/ready, sourced from deal.schedulingGuidance.scheduleByDate

  // === Serial number / barcode tracking (V107.52.30+) ===
  serialNumber?: string; // Serial number or barcode captured at AT_OFFICE receipt (legacy single-serial — first serial when qty > 1)
  serialNumbers?: string[]; // Multi-serial capture for items with quantity > 1 (e.g. 5x cameras = 5 serials)
  serialCapturedAt?: string; // ISO timestamp when serial was captured
  serialCapturedBy?: string; // userId who captured the serial

  // === Projects v2 — wave + dwelling linkage (§1.4) ===
  // Moved here in the P1 audit pass: schema.ts's ProvisioningItem is a legacy
  // nested-in-Job shape; the actually-used ProvisioningItem (this one) is the
  // sub-collection interface Phase 6 will read.
  projectId?: string;
  projectDwellingId?: string;
  waveNumber?: number;

  // === Projects v2 — variation linkage + cancellation (P7.4) ===
  // sourceVariationId: set when an item was spawned by an approved
  // variation's add line item. cancelled + cancelledReason +
  // cancelledVariationId: a variation's remove line item soft-cancels
  // matching items (never deletes — audit trail). The cost roll-up
  // (P9a.1) excludes cancelled items from actuals; the WaveBoard filters
  // them out of the Kanban.
  sourceVariationId?: string;
  cancelled?: boolean;
  cancelledReason?: 'variation_removal' | string;
  cancelledVariationId?: string;
  /** Phase 9d billing-flow field — actual invoiced cost once a supplier
   *  invoice lands. Read by the cost roll-up's actualCost. Undefined
   *  until Phase 9d writes it. */
  actualInvoicedCost?: number;
  /** P9d — how the actual cost was captured + when + against which invoice.
   *  Written by the "Record actual cost" affordance on the Financials dash. */
  actualInvoicedCostSource?: 'supplier-invoice' | 'manual';
  actualInvoicedAt?: string; // ISO timestamp the cost was recorded
  invoiceReference?: string; // Supplier invoice number/reference

  createdAt: string | Timestamp; // ISO string (preferred) or Firestore Timestamp (legacy)
  updatedAt: string | Timestamp; // ISO string (preferred) or Firestore Timestamp (legacy)
}

// ============================================================================
// INTERNAL ORDERS — stock + office supply ordering (V107.53)
// ============================================================================

/**
 * Collection: internal_orders/{id}
 *
 * A free-form internal purchase order that lives on the Provisioning page,
 * deliberately SEPARATE from the deal/fault-driven `provisioning_items`
 * flow. Built for the everyday "office run" — a mix of free-text notes and
 * structured line items. e.g. stock to replenish (Cat6 cable, RJ45 ends)
 * sitting alongside office consumables (milk, coffee). Not tied to any
 * customer, deal, or fault.
 *
 * Lives under the auth-required baseline in firestore.rules (NOT one of the
 * tenant-isolated collections), but every write still carries the canonical
 * tenantId via getTenantIdSync() for forward-compat + correct multi-tenant
 * reads (tenantIdReadUnion()).
 */
export type InternalOrderStatus =
  | 'open'        // being built / not yet ordered
  | 'ordered'     // placed with supplier(s)
  | 'received'    // everything's arrived
  | 'cancelled';  // abandoned

export type InternalOrderCategory =
  | 'stock'       // business inventory — cables, connectors, mounts
  | 'office'      // office consumables — milk, coffee, stationery
  | 'mixed'       // a bit of both
  | 'other';

export const INTERNAL_ORDER_STATUS_LABELS: Record<InternalOrderStatus, string> = {
  open: 'Open',
  ordered: 'Ordered',
  received: 'Received',
  cancelled: 'Cancelled',
};

export const INTERNAL_ORDER_CATEGORY_LABELS: Record<InternalOrderCategory, string> = {
  stock: 'Stock',
  office: 'Office',
  mixed: 'Mixed',
  other: 'Other',
};

/** A single structured line on an internal order. */
export interface InternalOrderLineItem {
  id: string;            // local uuid (crypto.randomUUID)
  name: string;          // "Cat6 cable — 305m box"
  /**
   * @deprecated Legacy single quantity. Superseded by `quantityNeeded`
   * (May 2026). Still read for back-compat — `quantityNeeded ?? quantity`
   * — but no longer written; lines are normalised to the two-field shape
   * the first time they're touched.
   */
  quantity?: number;
  quantityNeeded?: number;   // how many we need to order
  quantityOrdered?: number;  // how many have actually been ordered so far
  supplier?: string;         // where to get it
  notes?: string;            // size, colour, spec, etc.
  done?: boolean;            // checked off → cleared from the active checklist
  doneAt?: string;           // ISO — when it was cleared (audit / sort)
}

export interface InternalOrder {
  id?: string;
  tenantId: string;
  title: string;                       // "Weekly stock + office run"
  category: InternalOrderCategory;
  status: InternalOrderStatus;
  notes?: string;                      // the FREE-WRITING part — anything goes
  lineItems: InternalOrderLineItem[];  // structured items
  supplier?: string;                   // optional default / primary supplier
  neededBy?: string;                   // YYYY-MM-DD — optional deadline

  // Audit
  createdAt: string;                   // ISO
  updatedAt: string;                   // ISO
  createdBy?: string;                  // userId
  createdByName?: string;              // denormalised display name
  orderedAt?: string;                  // ISO — stamped on → ordered
  receivedAt?: string;                 // ISO — stamped on → received
}

/**
 * Collection: serial_numbers/{id}
 * Cross-entity serial number index — allows querying serial numbers from ANY entity.
 * Created when a provisioning item receives a serial number at AT_OFFICE stage.
 *
 * Relationships (stored as string IDs):
 * - provisioningItemId → provisioning_items/{itemId}
 * - dealId → deals/{dealId}
 * - customerId → customers/{customerId}
 * - jobIds → jobs/{jobId}  (install jobs linked to the deal)
 * - faultId → faults/{faultId}  (if item was provisioned for a fault)
 * - serviceIds → customer_services/{serviceId}  (populated on service activation)
 */
export interface SerialNumberRecord {
  id?: string;
  serialNumber: string;
  itemName: string;
  provisioningItemId: string;
  dealId?: string;
  customerId?: string;
  customerName?: string;
  jobIds?: string[];
  faultId?: string;
  serviceIds?: string[];
  supplier?: string;
  capturedAt: string;
  capturedBy: string;
  capturedByName?: string;
  tenantId: string;
}

/**
 * Collection: tech_requests/{requestId}
 * Support log for technical questions and requests
 *
 * Source entity — one of:
 * - dealId: linked to deals/{dealId}
 * - serviceRequestId: linked to service_requests/{id} (tagged from service request UI)
 *
 * The tech inbox shows every OPEN tech request to every tech. `targetedUserIds`
 * is denormalised for future routing / highlighting but is not used to filter.
 */
export interface TechRequest {
  id?: string; // Firestore doc ID (set after creation)
  dealId?: string; // Foreign key to Deal (optional — may be sourced from a service request instead)
  serviceRequestId?: string; // Foreign key to service_requests/{id}
  serviceRequestTitle?: string; // Denormalised for display
  customerName?: string; // Denormalised for display
  priority: TechRequestPriority;
  status: TechRequestStatus;
  question: string;
  answer?: string;
  createdAt: string | Timestamp; // ISO string (preferred) or Firestore Timestamp (legacy)
  updatedAt: string | Timestamp; // ISO string (preferred) or Firestore Timestamp (legacy)
  createdBy?: string; // User ID
  createdByName?: string; // Denormalised author name
  targetedUserIds?: string[]; // Users who were @tagged (for display / future per-tech routing)
  targetedUserNames?: string[]; // Denormalised names matching targetedUserIds
  resolvedAt?: string | Timestamp; // ISO string (preferred) or Firestore Timestamp (legacy)
  resolvedBy?: string; // User ID
  tenantId?: string; // Stamped on create (getTenantIdSync) — tech_requests is tenant-isolated
}

// V107.52.36 Phase 3 — `PartsCatalogItem` removed from this file. Import
// from '@/types' (canonical) instead. The schema.ts version carries the
// full unified-catalog surface (image fields, whyChosen, specs, kind,
// bundleItems, Projects v2 cost fields) that this duplicate never had.

/**
 * Collection: presite_parts/{selectionId}
 * Parts selected during presite job for quoting
 *
 * Relationships (stored as string IDs):
 * - dealId: Reference to deals/{dealId}
 * - partId: Reference to parts_catalog/{partId} (optional - can be custom part)
 */
export interface PresitePartsSelection {
  id?: string; // Firestore doc ID (set after creation)
  dealId: string; // Foreign key reference to Deal
  partId?: string; // Foreign key reference to PartsCatalogItem (if from catalog)
  partName: string; // Part name (from catalog or custom)
  sku?: string; // SKU/model
  quantity: number; // Quantity needed
  notes?: string; // Installation notes or special requirements
  isCustomPart?: boolean; // True if not from catalog
  estimatedPrice?: number; // Estimated price per unit
  metadata?: Record<string, unknown>; // Flexible metadata object
  addedToPlayground?: boolean; // Whether this part was added in playground
  addedToProvisioning?: boolean; // Whether this part was converted to provisioning item
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string; // User ID (technician who selected it)
}

// V107.52.36 Phase 3 — `Job` removed from this file. Import from
// '@/types' (canonical) instead. The schema.ts version carries the full
// runtime surface (fieldLog, installChecklist, fieldScope, aiBriefing,
// briefingAcknowledged, etaTextSent, overrun*, schedulingNotes,
// aiBriefingEdited, contactStatus, etc.) that this duplicate never had.

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Phase 0 (trade-pack architecture): legacy guard that matches ONLY the
 * built-in DealCategory enum values. Pack-introduced categories return
 * false here — that's correct, this is the "is this one of the original
 * telco categories?" guard, not a generic category validator.
 *
 * For pack-aware validation, check that the key exists in the active pack's
 * categories via useServiceCategories() (Phase 1+ work).
 */
export function isDealCategory(value: string): value is DealCategory {
  return Object.values(DealCategory).includes(value as DealCategory);
}

export function isDealComplexity(value: string): value is DealComplexity {
  return Object.values(DealComplexity).includes(value as DealComplexity);
}

export function isDealStage(value: string): value is DealStage {
  return Object.values(DealStage).includes(value as DealStage);
}

export function isJobType(value: string): value is JobType {
  return Object.values(JobType).includes(value as JobType);
}

export function isStarlinkKitOption(value: string): value is StarlinkKitOption {
  return Object.values(StarlinkKitOption).includes(value as StarlinkKitOption);
}
